import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Building2,
  MapPin,
  BedDouble,
  Calendar,
  FileText,
  Plus,
  ArrowLeft,
  Pencil,
  Trash2,
  TrendingUp,
  DollarSign,
  Percent,
  GitCompare,
  Copy,
} from "lucide-react";
import { toast } from "sonner";

export default function PropertyDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [property, setProperty] = useState(null);
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Copy Budget Modal State
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [allBudgetsForCopy, setAllBudgetsForCopy] = useState([]);
  const [selectedSourceBudget, setSelectedSourceBudget] = useState("");
  const [copyBudgetName, setCopyBudgetName] = useState("");
  const [copyRoomCount, setCopyRoomCount] = useState("");
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    fetchPropertyData();
  }, [id]);

  const fetchPropertyData = async () => {
    try {
      const [propertyRes, budgetsRes] = await Promise.all([
        axios.get(`${API}/properties/${id}`, { headers: getAuthHeader() }),
        axios.get(`${API}/budgets?property_id=${id}`, { headers: getAuthHeader() }),
      ]);
      setProperty(propertyRes.data);
      
      // Sort budgets by created_at date, latest first
      const sortedBudgets = (budgetsRes.data || []).sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setBudgets(sortedBudgets);
    } catch (error) {
      toast.error("Failed to load property");
      navigate("/properties");
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetsForCopy = async () => {
    try {
      const response = await axios.get(`${API}/budgets/all-for-copy`, {
        headers: getAuthHeader(),
      });
      setAllBudgetsForCopy(response.data);
    } catch (error) {
      toast.error("Failed to load budgets");
    }
  };

  const handleOpenCopyModal = () => {
    fetchBudgetsForCopy();
    setCopyRoomCount(property?.total_rooms?.toString() || "");
    setCopyBudgetName("");
    setSelectedSourceBudget("");
    setShowCopyModal(true);
  };

  const handleCopyBudget = async () => {
    if (!selectedSourceBudget) {
      toast.error("Please select a budget to copy from");
      return;
    }
    if (!copyRoomCount || parseInt(copyRoomCount) < 1) {
      toast.error("Please enter a valid number of rooms");
      return;
    }

    setCopying(true);
    try {
      await axios.post(
        `${API}/budgets/copy`,
        {
          source_budget_id: selectedSourceBudget,
          target_property_id: id,
          budget_name: copyBudgetName || null,
          total_rooms: parseInt(copyRoomCount),
        },
        { headers: getAuthHeader() }
      );
      toast.success("Budget copied successfully!");
      setShowCopyModal(false);
      fetchPropertyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to copy budget");
    } finally {
      setCopying(false);
    }
  };

  const handleDeleteBudget = async (budgetId) => {
    if (!confirm("Are you sure you want to delete this budget?")) return;
    try {
      await axios.delete(`${API}/budgets/${budgetId}`, {
        headers: getAuthHeader(),
      });
      toast.success("Budget deleted successfully");
      fetchPropertyData();
    } catch (error) {
      toast.error("Failed to delete budget");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full spinner" />
        </div>
      </Layout>
    );
  }

  if (!property) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Property not found</p>
        </div>
      </Layout>
    );
  }

  // Calculate some stats from budgets
  const latestBudget = budgets[0];
  let avgOccupancy = 0;
  let avgAdr = 0;
  if (latestBudget) {
    const monthlyData = latestBudget.monthly_data || [];
    avgOccupancy = monthlyData.reduce((sum, m) => sum + m.occupancy_rate, 0) / (monthlyData.length || 1);
    avgAdr = monthlyData.reduce((sum, m) => sum + m.adr, 0) / (monthlyData.length || 1);
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="property-detail-page">
        {/* Back Button */}
        <Link
          to="/properties"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Properties
        </Link>

        {/* Property Header */}
        <div className="relative rounded-xl overflow-hidden">
          <div className="h-48 md:h-64 bg-gradient-to-br from-slate-800 to-slate-900">
            <img
              src="https://images.unsplash.com/photo-1765439178218-e54dcbb64bcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3RlbCUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlJTIwbmlnaHR8ZW58MHx8fHwxNzcwNjA4MTcwfDA&ixlib=rb-4.1.0&q=85"
              alt={property.name}
              className="w-full h-full object-cover opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-transparent" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
              <div>
                <Badge className="bg-white/20 text-white text-xs mb-3">
                  {property.property_type}
                </Badge>
                <h1
                  className="text-3xl md:text-4xl font-bold text-white"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {property.name}
                </h1>
                <div className="flex items-center gap-4 mt-2 text-slate-300">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    {property.location}
                  </div>
                  <div className="flex items-center gap-1">
                    <BedDouble className="w-4 h-4" />
                    {property.total_rooms} rooms
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Link to={`/budget/${property.id}`}>
                  <Button
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="create-new-budget-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create New Budget
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                  onClick={handleOpenCopyModal}
                  data-testid="copy-budget-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Budget
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {latestBudget && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">
                      Avg Occupancy
                    </p>
                    <p
                      className="text-2xl font-bold text-slate-900 mt-1"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {avgOccupancy.toFixed(1)}%
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      FY {latestBudget.fiscal_year_start}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Percent className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">
                      Avg Daily Rate
                    </p>
                    <p
                      className="text-2xl font-bold text-slate-900 mt-1"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {formatCurrency(avgAdr)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      FY {latestBudget.fiscal_year_start}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">
                      Total Budgets
                    </p>
                    <p
                      className="text-2xl font-bold text-slate-900 mt-1"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {budgets.length}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Created for this property
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Budgets List */}
        <div>
          <h2
            className="text-xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Budget History
          </h2>

          {budgets.length === 0 ? (
            <Card className="border-slate-200 border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                  <FileText className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">
                  No budgets yet
                </h3>
                <p className="text-slate-500 text-center max-w-sm mb-4">
                  Create your first budget to generate financial projections for
                  this property.
                </p>
                <Link to={`/budget/${property.id}`}>
                  <Button className="bg-amber-600 hover:bg-amber-700">
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Budget
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {budgets.map((budget, index) => (
                <Card
                  key={budget.id}
                  className="border-slate-200 hover:shadow-md transition-shadow"
                  data-testid={`budget-card-${index}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-slate-900 flex items-center justify-center relative">
                          <Calendar className="w-6 h-6 text-white" />
                          {index === 0 && (
                            <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full font-medium">
                              Latest
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-lg font-semibold text-slate-900">
                              {budget.budget_name || `Fiscal Year ${budget.fiscal_year_start}`}
                            </h3>
                            {budget.copied_from && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                Copied
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              {new Date(budget.created_at).toLocaleDateString('en-AU', {
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                            <span className="flex items-center gap-1">
                              <BedDouble className="w-3.5 h-3.5" />
                              {budget.total_rooms_at_creation || property.total_rooms} rooms
                            </span>
                            {budget.budget_name && (
                              <span className="text-slate-400">
                                FY {budget.fiscal_year_start}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right mr-4">
                          <p className="text-sm text-slate-500">GST Rate</p>
                          <p className="font-medium text-slate-900">
                            {budget.gst_rate}%
                          </p>
                        </div>
                        <Link to={`/reports/${budget.id}`}>
                          <Button
                            className="bg-amber-600 hover:bg-amber-700"
                            data-testid={`view-report-${index}`}
                          >
                            <TrendingUp className="w-4 h-4 mr-2" />
                            View Reports
                          </Button>
                        </Link>
                        <Link to={`/compare/${budget.id}`}>
                          <Button
                            variant="outline"
                            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                            data-testid={`compare-budget-${index}`}
                          >
                            <GitCompare className="w-4 h-4 mr-2" />
                            Compare Budgets
                          </Button>
                        </Link>
                        <Link to={`/budget/${property.id}/edit/${budget.id}`}>
                          <Button variant="outline" size="icon" data-testid={`edit-budget-btn`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteBudget(budget.id)}
                          data-testid={`delete-budget-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Copy Budget Modal */}
      <Dialog open={showCopyModal} onOpenChange={setShowCopyModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-amber-600" />
              Copy Budget
            </DialogTitle>
            <DialogDescription>
              Copy an existing budget to this property. You can adjust the number of rooms and give it a custom name.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Source Budget Selection */}
            <div className="space-y-2">
              <Label htmlFor="source-budget">Copy from</Label>
              <Select value={selectedSourceBudget} onValueChange={setSelectedSourceBudget}>
                <SelectTrigger id="source-budget" data-testid="source-budget-select">
                  <SelectValue placeholder="Select a budget to copy from" />
                </SelectTrigger>
                <SelectContent>
                  {allBudgetsForCopy.map((property) => (
                    <div key={property.property_id}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50">
                        {property.property_name} ({property.total_rooms} rooms)
                      </div>
                      {property.budgets.map((budget) => (
                        <SelectItem
                          key={budget.id}
                          value={budget.id}
                          className="pl-4"
                        >
                          {budget.budget_name || `FY ${budget.fiscal_year_start}`}
                          {" - "}
                          {budget.total_rooms_at_creation || property.total_rooms} rooms
                          <span className="text-slate-400 ml-2">
                            ({new Date(budget.created_at).toLocaleDateString('en-AU', { month: 'short', year: 'numeric' })})
                          </span>
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Budget Name */}
            <div className="space-y-2">
              <Label htmlFor="budget-name">Budget Name (optional)</Label>
              <Input
                id="budget-name"
                placeholder="e.g., 2025 Expansion Budget"
                value={copyBudgetName}
                onChange={(e) => setCopyBudgetName(e.target.value)}
                data-testid="budget-name-input"
              />
              <p className="text-xs text-slate-500">
                Leave blank to use the fiscal year as the name
              </p>
            </div>

            {/* Number of Rooms */}
            <div className="space-y-2">
              <Label htmlFor="room-count">Number of Rooms</Label>
              <Input
                id="room-count"
                type="number"
                min="1"
                placeholder="Enter number of rooms"
                value={copyRoomCount}
                onChange={(e) => setCopyRoomCount(e.target.value)}
                data-testid="room-count-input"
              />
              <p className="text-xs text-slate-500">
                Current property has {property?.total_rooms} rooms
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCopyModal(false)}
              disabled={copying}
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleCopyBudget}
              disabled={copying || !selectedSourceBudget}
              data-testid="copy-budget-submit"
            >
              {copying ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Copying...
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Copy Budget
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
